import React from 'react';
import { IBaseAction } from '@models/actions/IBaseAction';
import { ModalSize } from '@srm/ui/types/ModalSize';
import { IActionExecutor } from './IActionExecutor';

/** Всё, из чего экзекьютор собирает пропсы своей модалки. */
export interface IModalExecutorContext {
    objId: string;
    parentId: string | undefined;
    action: IBaseAction;
    rowData?: any;
    completeHandler?: (isSucceed: boolean) => void;
    /** `modalSize` из формулы → `action.options.modalSize` → дефолт экзекьютора */
    modalSize: ModalSize;
    /** `action.options.okButtonText` → «ОК» */
    okButtonText: string;
    /** `action.options.cancelButtonText` → «Отмена» */
    cancelButtonText: string;
}

export interface IModalExecutorOptions<TProps> {
    /** Модалка действия */
    modal: React.ComponentType<TProps>;
    /** Размер модалки, если его не задали ни формула размера, ни настройки действия */
    defaultModalSize: ModalSize;
    /** Сборка пропсов модалки из контекста действия */
    mapProps: (ctx: IModalExecutorContext) => TProps;
    /**
     * Что рисовать, пока действие не запускалось. По умолчанию — пустой фрагмент;
     * часть экзекьюторов исторически рисует здесь пустой `<div/>`.
     */
    emptyElement?: React.ReactElement;
}

export const buildModalExecutorContext = <TProps,>(
    { defaultModalSize }: IModalExecutorOptions<TProps>,
    objId: string,
    parentId: string | undefined,
    action: IBaseAction,
    rowData: any,
    completeHandler: ((isSucceed: boolean) => void) | undefined,
    modalSize: ModalSize | null | undefined,
): IModalExecutorContext => ({
    objId,
    parentId,
    action,
    rowData,
    completeHandler,
    modalSize: modalSize ?? action.options?.modalSize ?? defaultModalSize,
    okButtonText: action.options?.okButtonText ?? 'ОК',
    cancelButtonText: action.options?.cancelButtonText ?? 'Отмена',
});

/**
 * Экзекьютор, вся работа которого — собрать пропсы и показать модалку.
 * Фабрики действий создают его через `new`, поэтому возвращается класс.
 * Действия с собственной логикой (запросы, навигация, подпись) остаются классами.
 *
 * Варианты поверх проверок accessibility — `createDocModalExecutor`
 * и `createActivityModalExecutor`; они лежат отдельно, потому что тянут за собой
 * базовые классы, а те замкнуты в цикл импортов с сервисами действий.
 */
export const createModalExecutor = <TProps extends object>(options: IModalExecutorOptions<TProps>) => {
    const Modal = options.modal;
    const empty = options.emptyElement ?? <></>;

    return class implements IActionExecutor {
        private _modalProps?: TProps;

        run = (
            objId: string,
            parentId: string | undefined,
            action: IBaseAction,
            rowData?: any,
            completeHandler?: (isSucceed: boolean) => void,
            _raiseVisualElement?: () => void,
            modalSize?: ModalSize | null,
        ) => {
            this._modalProps = options.mapProps(
                buildModalExecutorContext(options, objId, parentId, action, rowData, completeHandler, modalSize),
            );
        };

        visualElement = () => (this._modalProps ? <Modal {...this._modalProps} /> : empty);
    };
};
